import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { LocalDate } from '@/components/LocalDate'
import { ConfirmDialog } from '@/components/organization/ConfirmDialog'
import { PageHeader } from '@/components/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { customerLabel } from '@/lib/support/chat'
import {
  deleteChatNote,
  deleteChatTicket,
  getChatTicket,
} from '@/lib/support/chat-server'
import {
  ChannelBadge,
  IdentityBadge,
  PriorityBadge,
  StatusBadge,
} from './-components/chat/badges'
import { ChatComposer } from './-components/chat/ChatComposer'
import {
  PreviousTicketsCard,
  TicketContextCard,
  TicketCustomerCard,
  TicketPropertiesCard,
} from './-components/chat/ChatSidebar'
import { ChatThread } from './-components/chat/ChatThread'

export const Route = createFileRoute(
  '/_authenticated/admin/support_/chats_/$ticket',
)({
  component: AdminChatTicketPage,
})

function AdminChatTicketPage() {
  const { ticket: ref } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data, isPending, error } = useQuery({
    queryKey: ['admin-chat-ticket', ref],
    queryFn: () => getChatTicket({ data: { ticket: ref } }),
    // The customer's new messages arrive by polling; 10s keeps a live chat
    // usable without hammering PostHog.
    refetchInterval: 10_000,
  })
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-chat-ticket', ref] })

  const removeTicket = useMutation({
    mutationFn: () => deleteChatTicket({ data: { ticket: ref } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-chat-tickets'] })
      toast.success('Ticket deleted')
      void navigate({ to: '/admin/support/chats' })
    },
    onError: (e) =>
      toast.error("Couldn't delete the ticket", {
        description: (e as Error).message,
      }),
  })

  const removeNote = useMutation({
    mutationFn: (messageId: string) =>
      deleteChatNote({ data: { ticket: ref, messageId } }),
    onSuccess: () => {
      void refresh()
      toast.success('Note deleted')
    },
    onError: (e) =>
      toast.error("Couldn't delete the note", {
        description: (e as Error).message,
      }),
  })

  const ticket = data?.ticket

  return (
    <div className="flex flex-col gap-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
        <Link to="/admin/support/chats">
          <ArrowLeft data-icon="inline-start" />
          All chats
        </Link>
      </Button>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the ticket</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      <PageHeader
        title={ticket ? `#${ticket.number} · ${customerLabel(ticket)}` : 'Chat'}
        description={
          ticket ? (
            <span className="flex flex-wrap items-center gap-1.5">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <ChannelBadge channel={ticket.channel} />
              <IdentityBadge verified={ticket.identityVerified} />
              <span className="text-muted-foreground text-sm">
                Opened <LocalDate iso={ticket.createdAt} withTime />
              </span>
            </span>
          ) : isPending ? (
            'Loading…'
          ) : undefined
        }
        actions={
          ticket ? (
            <>
              <Button asChild variant="outline">
                <a href={ticket.posthogUrl} target="_blank" rel="noreferrer">
                  Open in PostHog
                  <ExternalLink data-icon="inline-end" />
                </a>
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={removeTicket.isPending}
              >
                <Trash2 data-icon="inline-start" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      {ticket && data ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Card className="gap-0 overflow-hidden py-0">
            <div className="h-[32rem]">
              <ChatThread
                ticket={ticket}
                messages={data.messages}
                onDeleteNote={(id) => removeNote.mutate(id)}
                deletingNoteId={
                  removeNote.isPending ? removeNote.variables : null
                }
              />
            </div>
            <Separator />
            <div className="p-3">
              <ChatComposer ticket={ticket} onSent={() => void refresh()} />
            </div>
          </Card>
          <div className="flex flex-col gap-6">
            <TicketCustomerCard ticket={ticket} account={data.account} />
            <TicketPropertiesCard ticket={ticket} />
            <TicketContextCard ticket={ticket} />
            <PreviousTicketsCard tickets={data.previousTickets} />
          </div>
        </div>
      ) : isPending ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Skeleton className="h-[40rem]" />
          <div className="flex flex-col gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-64" />
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={ticket ? `Delete ticket #${ticket.number}?` : 'Delete ticket?'}
        description="The conversation disappears from this inbox and from PostHog, which purges it after its grace period. The customer is not notified."
        actionLabel="Delete ticket"
        variant="destructive"
        onConfirm={() => removeTicket.mutate()}
      />
    </div>
  )
}
